// Order Data Transformer
// Maps API order data format to app order format

/**
 * Transform API order response to app order format
 * @param {object} apiOrder - Order data from API
 * @returns {object} Transformed order object
 */
export const transformOrder = (apiOrder) => {
  if (!apiOrder) return null;

  // Extract address data (new API uses 'location' field)
  const address = apiOrder.address || {};
  const customer = apiOrder.customer || apiOrder.user || {};
  
  // Parse schedule date from order_picked_date
  const scheduleDate = apiOrder.order_picked_date || apiOrder.scheduled_date || apiOrder.created_at;
  let scheduleDateFormatted = 'Today';
  let scheduleDateFull = '';
  if (scheduleDate) {
    const date = new Date(scheduleDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const orderDate = new Date(date);
    orderDate.setHours(0, 0, 0, 0);
    
    const diffTime = orderDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Full date format: "Tuesday, 26 January 2026"
    scheduleDateFull = date.toLocaleDateString('en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    
    if (diffDays === 0) {
      scheduleDateFormatted = 'Today';
    } else if (diffDays === 1) {
      scheduleDateFormatted = 'Tomorrow';
    } else {
      scheduleDateFormatted = date.toLocaleDateString('en-IN', { 
        day: 'numeric', 
        month: 'short' 
      });
    }
  }

  // Format time from schedule_period (e.g., "evening", "Morning", "Evening 4 PM - 6 PM")
  const schedulePeriod = apiOrder.schedule_period || '';
  let startTime = '9:00 AM';
  let endTime = '11:00 AM';
  
  if (schedulePeriod) {
    // Check if it contains time range (e.g., "Evening 4 PM - 6 PM")
    const timeRangeMatch = schedulePeriod.match(/(\d{1,2}\s*(?:AM|PM))\s*-\s*(\d{1,2}\s*(?:AM|PM))/i);
    if (timeRangeMatch) {
      startTime = timeRangeMatch[1].trim();
      endTime = timeRangeMatch[2].trim();
    } else {
      // Map period names to time ranges
      const periodLower = schedulePeriod.toLowerCase();
      if (periodLower.includes('morning')) {
        startTime = '9:00 AM';
        endTime = '12:00 PM';
      } else if (periodLower.includes('afternoon')) {
        startTime = '12:00 PM';
        endTime = '4:00 PM';
      } else if (periodLower.includes('evening')) {
        startTime = '4:00 PM';
        endTime = '7:00 PM';
      } else if (periodLower.includes('night')) {
        startTime = '7:00 PM';
        endTime = '9:00 PM';
      }
    }
  } else {
    // Fallback to scheduled_time if available
    const scheduledTime = apiOrder.scheduled_time || '';
    if (scheduledTime) {
      const times = scheduledTime.split('-');
      startTime = times[0]?.trim() || '9:00 AM';
      endTime = times[1]?.trim() || '11:00 AM';
    }
  }

  // Calculate distance if coordinates are available
  let distance = null;
  if (address.latitude && address.longitude) {
    // In a real app, you'd calculate distance from user's current location
    // For now, we'll use a placeholder
    distance = '2.4 km';
  }

  // Build full address string (new API uses 'location' field)
  const addressParts = [
    address.location, // New API uses 'location' instead of 'address_line1'
    address.landmark,
    address.state,
    address.pincode
  ].filter(Boolean);
  const fullAddress = addressParts.join(', ') || 'Address not available';

  // Calculate estimated weight from order_products or products
  let estimatedWeight = 0;
  const orderProducts = apiOrder.order_products || apiOrder.products || [];
  if (Array.isArray(orderProducts)) {
    estimatedWeight = orderProducts.reduce((sum, product) => {
      const quantity = parseFloat(product.quantity) || 0;
      return sum + quantity;
    }, 0);
  }

  return {
    id: apiOrder.order_id?.toString() || apiOrder.id?.toString() || '',
    order_id: apiOrder.order_id || apiOrder.id,
    userId: customer.user_id?.toString() || customer.id?.toString() || '',
    user_id: customer.user_id || customer.id,
    addressId: address.address_id?.toString() || address.id?.toString() || '',
    address_id: address.address_id || address.id,
    status: apiOrder.order_status || apiOrder.status || 'PENDING',
    type: apiOrder.summary || apiOrder.order_type || apiOrder.type || 'Mixed Plastic Collection',
    schedule: {
      date: scheduleDateFormatted,
      dateFull: scheduleDateFull, // Full date format: "Tuesday, 26 January 2026"
      start: startTime,
      end: endTime,
      period: schedulePeriod, // Store original schedule_period
      duration: apiOrder.estimated_duration || '15m'
    },
    location: {
      address: fullAddress,
      landmark: address.landmark || '',
      pincode: address.pincode || '',
      state: address.state || '',
      country: address.country || 'India',
      lat: parseFloat(address.lat) || parseFloat(address.latitude) || null,
      lng: parseFloat(address.long) || parseFloat(address.longitude) || null,
      distance: distance,
      addressId: address.address_id?.toString() || address.id?.toString() || ''
    },
    volume: estimatedWeight > 0 ? `~${estimatedWeight}kg` : (apiOrder.estimated_weight ? `~${apiOrder.estimated_weight}kg` : '~12kg'),
    description: apiOrder.summary || apiOrder.description || apiOrder.notes || 'Plastic Waste Collection',
    customer: {
      name: customer.name || 'Customer',
      phone: customer.phone_number || customer.phone || '',
      type: customer.user_type === 'BUSINESS' ? 'Business' : 'Consumer'
    },
    instructions: apiOrder.user_note || apiOrder.special_instructions || apiOrder.instructions || '',
    items: apiOrder.order_products || apiOrder.products || apiOrder.items || [],
    completedDate: apiOrder.completed_at || apiOrder.order_updated_at || apiOrder.updated_at,
    created_at: apiOrder.order_created_at || apiOrder.created_at,
    updated_at: apiOrder.order_updated_at || apiOrder.updated_at,
    picker_id: apiOrder.picker_id,
    order_products: apiOrder.order_products || apiOrder.products || []
  };
};

/**
 * Transform array of API orders to app format
 * @param {array} apiOrders - Array of order data from API
 * @returns {array} Array of transformed order objects
 */
export const transformOrders = (apiOrders) => {
  if (!Array.isArray(apiOrders)) {
    return [];
  }
  
  return apiOrders.map(order => transformOrder(order)).filter(Boolean);
};

